<template>
  <b-modal
    id="modal-templates"
    ref="bModal"
    size="xxl"
    :title="$t('global.description.modal.templates')"
    centered
    modal-class="templates-modal"
    qa="text-templates-modal"
    static
    lazy
    @hidden="onHidden"
    @show="init"
  >
    <!--Table view: item selection-->
    <div>
      <b-row v-if="!hideTemplatesList">
        <b-col class="filters-header bottom-bordered pb-2">
          <b-form-input
            v-model="titleFilter"
            qa="text-templates-modal-filter-input"
            type="search"
            :placeholder="$t('global.description.modal.filterByTitleAndDescription')"
            class="filter-input form-control-sm"
          />
        </b-col>
        <b-col
          v-if="compatibleSwitchVisible"
          qa="text-templates-modal-checkbox"
          cols="auto"
          class="filters-header mt-1"
        >
          <b-form-checkbox
            v-model="customConfig.showOnlyCompatible"
            name="checkbox-text"
          >
            <span class="dark">{{ $t('global.description.modal.compatible') }}</span>
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="px-0">
          <div class="horizontal-divider" />
        </b-col>
      </b-row>
      <div
        class="text-templates-table full-bordered"
        qa="text-templates-modal-table"
      >
        <b-container fluid>
          <b-row
            v-if="!hideTemplatesList"
            class="table-header component-inside-1 full-bordered d-flex align-items-center"
          >
            <b-col
              cols="3"
              class="label-dark"
            >
              {{ $t('global.description.modal.title') }}
            </b-col>
            <b-col class="label-dark">
              {{ $t('global.description.modal.description') }}
            </b-col>
            <b-col
              cols="2"
              class="label-dark"
            >
              {{ $t('global.description.modal.textModeAllowed') }}
            </b-col>
          </b-row>
          <b-row class="table-content left-bordered right-bordered bottom-bordered">
            <b-col>
              <b-row
                v-for="template in filteredTemplates"
                :key="template.id"
                class="table-row bottom-bordered py-3 px-2"
                :class="{'row-active': selectedTemplateId === template.id}"
                qa="text-templates-modal-table-row"
                @click="selectTemplate(template.id)"
              >
                <b-col
                  class="table-row-tittle label-dark"
                  cols="3"
                  qa="text-templates-modal-table-row-title"
                >
                  {{ template.title }}
                </b-col>
                <b-col
                  class="table-row-desc label-dark"
                  qa="text-templates-modal-table-row-description"
                >
                  <div v-html="template.text" />
                </b-col>
                <b-col
                  cols="2"
                  class="label-dark"
                  qa="text-templates-modal-table-row-edition"
                >
                  {{ $t(template.textEditionAllowed.toString()) }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-container>
        <!--Template Edition View-->
        <div
          v-if="selectedTemplate"
          class="panel-visible active"
          :class="{ 'w-100': hideTemplatesList }"
        >
          <div class="template-sidepanel ap-sidepanel">
            <div class="header bottom-bordered px-4 py-2 d-flex align-items-center justify-content-between">
              <span
                class="ap-title ap-title__main"
                qa="text-templates-modal-sidepanel-title"
              >{{ selectedTemplate.title }}</span>
              <div v-if="!hideTemplatesList">
                <b-button
                  v-tooltip="$tc('global.configureTextTemplate')"
                  class="btn__icon btn__icon--primary px-2"
                  qa="text-templates-modal-sidepanel-configuration-button"
                  @click="onConfigureTextTemplateClick"
                >
                  <i
                    aria-hidden="true"
                    class="far fa-cog"
                  />
                </b-button>
                <b-button
                  class="btn__icon btn__icon--primary ml-2"
                  qa="text-templates-modal-sidepanel-close-button"
                  @click="selectedTemplate = null"
                >
                  <i
                    aria-hidden="true"
                    class="far fa-times"
                  />
                </b-button>
              </div>
            </div>
            <div
              class="text-edition pt-3 px-5 pb-4"
              :class="{'options-not-filled': fieldValidation }"
            >
              <b-form v-html="currentText" />
            </div>
            <div class="footer top-bordered p-2 d-flex justify-content-end">
              <div>
                <b-button
                  v-shortkey="['ctrl', 'enter']"
                  class="btn--primary"
                  size="sm"
                  qa="text-templates-modal-sidepanel-apply-button"
                  @shortkey.prevent="applyTemplateAndClose"
                  @click="applyTemplateAndClose"
                >
                  <span class="fal fa-plus mr-2" />
                  {{ $t('global.apply') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      slot="modal-footer"
      class="footer-banner px-2 w-100"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            v-if="hideTemplatesList"
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import templateResource from '@/api/resources/textTemplateService'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import Mustache from 'mustache'
import { cloneDeep, isPlainObject, transform } from 'lodash'
import VtComboSelect from '@/components/common/vt-combo-select/VtComboSelect'
import Vue from 'vue'
import VtInput from '@/components/common/VtInput'
import { DescriptionType } from '@/api/resources/description/description.model'
import { TextNormalization } from '@/shared/util/text-normalization'
import sanitizeHtml from 'sanitize-html'

const TEXT_TEMPLATES_UPDATE_CHANNEL = 'textTemplatesUpdateChannel'

export default {
    name: 'TextTemplatesModal',
    props: {
        laboratoryId: { type: Number, required: true },
        descriptionLaboratoryId: { type: Number, required: false },
        caseId: { type: Number, required: false },
        sampleId: { type: Number, required: false },
        templateIdInUse: { type: Number, required: false },
        optionsSelectedForTemplateInUse: { type: Object, required: false },
        uiComponentCode: { type: String, required: false },
        forBatchActionsModal: { type: Boolean, default: false },
        avoidValidations: { type: Boolean, default: false }
    },
    data() {
        return {
            selectedTemplateId: null,
            templates: [],
            customConfig: {
                showOnlyCompatible: true
            },
            titleFilter: '',
            selectedTemplate: null,
            selectedOptions: {},
            currentText: '',
            fieldValidation: false,
            loadCurrentTemplateDetails: true,
            broadcastUpdateTextTemplatesChannel: null
        }
    },
    computed: {
        hideTemplatesList() {
            return this.loadCurrentTemplateDetails && this.descriptionLaboratoryId
                ? this.laboratoryId !== this.descriptionLaboratoryId : false
        },
        compatibleSwitchVisible() {
            return this.caseId && this.sampleId && this.uiComponentCode
        },
        filteredTemplates() {
            const normalizedInputTokens = TextNormalization.removeDiacritics(this.titleFilter).split(' ')
            return this.templates.filter(template => template.active && (
                normalizedInputTokens.reduce((accumulator, normalizedInput) => {
                    return accumulator && (
                        TextNormalization.removeDiacritics(template.title).includes(normalizedInput) ||
                        TextNormalization.removeDiacritics(template.text).includes(normalizedInput))
                }, true)
            ))
        },
        applyButtonText() {
            if (this.selectedTemplate) {
                return this.selectedTemplate.textEditionAllowed ? this.$t('global.description.modal.add') : this.$t('global.description.modal.apply')
            } else {
                return ''
            }
        },
        applyCloseButtonText() {
            if (this.selectedTemplate) {
                return this.selectedTemplate.textEditionAllowed ? this.$t('global.description.modal.addClose') : this.$t('global.description.modal.applyClose')
            } else {
                return ''
            }
        }

    },
    watch: {
        'customConfig': {
            handler(newConfig) {
                if (this.compatibleSwitchVisible) {
                    customConfigResource.save(this.uiComponentCode, newConfig)
                }
            },
            deep: true
        },
        'customConfig.showOnlyCompatible': function() {
            this.loadTemplates()
        }
    },
    async created() {
        if (this.compatibleSwitchVisible) {
            const customConfig = await customConfigResource.get(this.uiComponentCode)
            if (customConfig) {
                this.customConfig = customConfig.config
            }
        }
    },
    beforeDestroy() {
        this.broadcastUpdateTextTemplatesChannel?.close()
    },
    methods: {
        onConfigureTextTemplateClick() {
            const routeData = this.$router.resolve({ name: 'textTemplates', params: { id: this.selectedTemplateId } })
            window.open(routeData.href, '_blank')
        },
        onHidden() {
            this.$emit('hidden')
        },
        hide() {
            this.broadcastUpdateTextTemplatesChannel.close()
            this.$refs.bModal.hide()
        },
        setBroadcastUpdateTextTemplatesChannel() {
            this.broadcastUpdateTextTemplatesChannel = new BroadcastChannel(TEXT_TEMPLATES_UPDATE_CHANNEL)
            this.broadcastUpdateTextTemplatesChannel.onmessage = async () => {
                await this.loadTemplates()
                await this.loadTemplateDetails()
            }
        },
        async show(loadCurrentTemplateDetails = true) {
            this.setBroadcastUpdateTextTemplatesChannel()
            this.loadCurrentTemplateDetails = loadCurrentTemplateDetails
            if (!this.loadCurrentTemplateDetails) this.cleanSelected()
            if (this.compatibleSwitchVisible) {
                const customConfig = await customConfigResource.get(this.uiComponentCode)
                if (customConfig) {
                    this.customConfig = customConfig.config
                }
            }
            this.$refs.bModal.show()
        },
        async selectTemplate(templateId) {
            this.selectedTemplateId = templateId
            await this.loadTemplateDetails()
            this.fieldValidation = false
        },
        async init() {
            this.titleFilter = ''
            await this.loadTemplates()
            if (this.templateIdInUse && this.loadCurrentTemplateDetails) {
                this.selectedTemplateId = this.templateIdInUse
                await this.loadTemplateDetails()
            }
        },
        async loadTemplates() {
            if (!this.hideTemplatesList) {
                let [error, response] = []
                if (this.customConfig.showOnlyCompatible && this.caseId && this.sampleId) {
                    [error, response] = await this.toCall(templateResource.findCompatibleIndexTemplates(this.laboratoryId, this.caseId, this.sampleId))
                } else {
                    [error, response] = await this.toCall(templateResource.findAllIndexTemplates(this.laboratoryId))
                }
                if (error) {
                    this.showErrorModalGlobal(this.$t('global.description.modal.error.loadingTemplates'))
                    return
                }
                this.templates = response
                this.selectedTemplateId = null
            }
        },
        async loadTemplateDetails() {
            this.selectedOptions = {}
            if (this.selectedTemplateId != null) {
                let [error, response] = await this.toCall(templateResource.findTemplateById(this.selectedTemplateId))
                if (error) {
                    this.showErrorModalGlobal(this.$t('global.description.modal.error.loadingTemplate'))
                    return
                }
                this.selectedTemplate = response
                if (this.loadCurrentTemplateDetails) this.loadCustomOptionsFromParams()
                this.loadCurrentTextWithComponents()
            } else {
                this.selectedTemplate = null
            }
        },
        loadCustomOptionsFromParams() {
            if (this.selectedTemplate && this.templateIdInUse &&
          this.selectedTemplate.id === this.templateIdInUse &&
          this.optionsSelectedForTemplateInUse) {
                const selectedOptionsAux = {}
                Object.keys(this.optionsSelectedForTemplateInUse).forEach(variable => {
                    const selectedOption = this.optionsSelectedForTemplateInUse[variable]
                    if (this.selectedTemplate.variablesAndOptions[variable]) {
                        if (this.selectedTemplate.variablesAndOptions[variable].length) {
                            const selectedOptionMapped = this.mapOptionsToComboSelect([selectedOption])?.[0]
                            if (!this.selectedTemplate.variablesAndOptions[variable].includes(selectedOption)) {
                                this.selectedTemplate.variablesAndOptions[variable].push(selectedOption)
                            }
                            selectedOptionsAux[variable] = selectedOptionMapped
                        } else {
                            selectedOptionsAux[variable] = selectedOption
                        }
                    }
                })
                this.selectedOptions = selectedOptionsAux
            }
        },
        transformOptionsSelected(selectedOptions) {
            return transform(selectedOptions, (result, value, key) => {
                if (isPlainObject(value)) {
                    result[key] = value.id
                } else {
                    result[key] = value
                }
            })
        },
        mapOptionsToComboSelect(options) {
            return options.map(option => {
                if (isPlainObject(option)) {
                    return option
                } else {
                    return {
                        id: option,
                        label: option
                    }
                }
            })
        },
        loadCurrentTextWithComponents() {
            let htmlText = ''
            const variableSpanPrefix = 'templateVariable-'
            const variables = []
            const textWithVariables = Mustache.parse(this.selectedTemplate.text)
            textWithVariables.forEach(templatePart => {
                if (templatePart[0] === 'text') {
                    htmlText += templatePart[1]
                } else {
                    variables.push(templatePart[1])
                    htmlText += `<span id="${variableSpanPrefix}${variables.length - 1}"></span>`
                }
            })
            this.currentText = htmlText
            this.$nextTick(() => {
                variables.forEach(variable => {
                    if (this.selectedTemplate.variablesAndOptions[variable].length > 0) {
                        const ComponentClass = Vue.extend(VtComboSelect)
                        const modelSelectInstance = new ComponentClass({
                            propsData: {
                                value: this.selectedOptions[variable],
                                options: this.mapOptionsToComboSelect(this.selectedTemplate.variablesAndOptions[variable]),
                                label: 'label',
                                taggable: true,
                                tagPlaceholder: this.$t('component.vueMultiselect.customOptionPlaceholder'),
                                placeholder: variable,
                                highlightWhenSelectedValue: true
                            },
                            parent: this
                        })
                        modelSelectInstance.$on('tag', function(addedElement) {
                            this.$parent.addCustomOption(addedElement, variable)
                            this.$set(this, 'value', addedElement)
                        })
                        modelSelectInstance.$on('select', function(selectedElement) {
                            this.$parent.selectedOptions[variable] = selectedElement
                            this.$set(this, 'value', selectedElement)
                        })
                        modelSelectInstance.$on('input', function(selectedElement) {
                            this.$parent.selectedOptions[variable] = selectedElement
                            this.$set(this, 'value', selectedElement)
                        })
                        modelSelectInstance.$on('remove', function() {
                            this.$parent.selectedOptions[variable] = null
                            this.$set(this, 'value', null)
                        })
                        modelSelectInstance.$mount('#' + variableSpanPrefix + variables.indexOf(variable))
                    } else {
                        const ComponentClass = Vue.extend(VtInput)
                        const inputInstance = new ComponentClass({
                            propsData: {
                                value: this.selectedOptions[variable],
                                type: 'text',
                                size: 'sm',
                                classes: 'field-edition',
                                name: '#' + variableSpanPrefix + variables.indexOf(variable),
                                highlightWhenSelectedValue: true
                            },
                            parent: this
                        })
                        inputInstance.$on('input', function(updatedValue) {
                            this.$parent.selectedOptions[variable] = updatedValue
                            this.$set(this, 'value', updatedValue)
                        })
                        inputInstance.$mount('#' + variableSpanPrefix + variables.indexOf(variable))
                        inputInstance.$el.setAttribute('class', 'field-edition')
                    }
                })
            })
        },
        addCustomOption(customOption, variable) {
            this.selectedTemplate.variablesAndOptions[variable].push(customOption)
            this.$nextTick(() => {
                this.selectedOptions[variable] = customOption
            })
        },
        applyTemplateAndClose() {
            const success = this.applyTemplate()
            if (success) {
                this.hide()
                this.fieldValidation = false
            }
        },
        applyTemplate() {
            const success = true
            if (this.avoidValidations) {
                const optionsSelected = this.transformOptionsSelected(this.selectedOptions)
                this.fillMissingOptions()
                const descriptionInfo = {
                    text: this.parseAndSetVariables(this.selectedTemplate.text, this.selectedOptions),
                    textEditingAllowed: this.selectedTemplate.textEditionAllowed,
                    optionsSelected: optionsSelected,
                    templateId: this.selectedTemplate.id,
                    descriptionType: DescriptionType.TEXT_TEMPLATE,
                    title: this.selectedTemplate.title
                }
                this.$emit('templateSelected', descriptionInfo)
                return success
            } else {
                if (!this.selectedTemplate.textEditionAllowed) {
                    if (!this.validSelectedOptions()) {
                        this.showErrorModalGlobal(this.$t('global.description.modal.error.optionsNotFilled'))
                        this.fieldValidation = true
                        return false
                    }
                } else {
                    this.fillMissingOptions()
                    this.fieldValidation = false
                }
                if (this.forBatchActionsModal) {
                    this.selectedTemplate.text = this.parseAndSetVariables(this.selectedTemplate.text, this.selectedOptions)
                    if (!this.selectedTemplate.textEditionAllowed) {
                        this.selectedTemplate.optionsSelected = this.transformOptionsSelected(this.selectedOptions)
                    }
                    this.$emit('templateSelected', this.selectedTemplate)
                    return success
                }
                const descriptionInfo = {
                    text: this.parseAndSetVariables(this.selectedTemplate.text, this.selectedOptions),
                    textEditingAllowed: this.selectedTemplate.textEditionAllowed,
                    templateId: this.selectedTemplate.id,
                    descriptionType: DescriptionType.TEXT_TEMPLATE
                }
                if (!this.selectedTemplate.textEditionAllowed) {
                    descriptionInfo.optionsSelected = this.transformOptionsSelected(this.selectedOptions)
                }

                this.$emit('templateSelected', descriptionInfo)

                return success
            }
        },
        parseAndSetVariables(templateText, options) {
            let optionsMapToMustacheRender = {}
            for (const option in options) {
                const currentOption = options[option]
                if (isPlainObject(currentOption)) {
                    optionsMapToMustacheRender[option] = currentOption.label
                } else {
                    optionsMapToMustacheRender[option] = currentOption
                }
            }
            return sanitizeHtml(Mustache.render(templateText, optionsMapToMustacheRender))
        },
        validSelectedOptions() {
            const hasOptionsNotFilled = Object.keys(this.selectedTemplate.variablesAndOptions).some(variable => {
                return !this.selectedOptions[variable]
            })
            return !hasOptionsNotFilled
        },
        fillMissingOptions() {
            Object.keys(this.selectedTemplate.variablesAndOptions).forEach(variable => {
                if (!this.selectedOptions[variable]) {
                    this.selectedOptions[variable] = '____'
                }
            })
        },
        cleanSelected() {
            this.selectedTemplateId = null
            this.selectedTemplate = null
            this.selectedOptions = {}
        }
    }
}
</script>

<style lang="scss">

  .templates-modal .modal-body {
    padding: 0.6rem 1.1rem 0 1.1rem !important;
    overflow: hidden;
  }

  .templates-modal .text-edition .multiselect {
    display: inline-block;
    width: auto;
    min-width: 100px;
    max-width: 400px;
  }

  .text-edition.options-not-filled .multiselect .multiselect__tags,
  .text-edition.options-not-filled .field-edition {
    border-color: vt-color(red-medium);
  }

  .templates-modal .multiselect__content-wrapper{
    width: auto !important;
    max-width: 200px !important;
    min-width: 100% !important;
  }

  .templates-modal .text-edition .multiselect .multiselect__single{
    font-size: 1rem;
    text-overflow: ellipsis;
  }

  .templates-modal .field-edition {
    width: 100px;
    display: inline-block;
    font-size: 1rem;
    border: none;
    border-bottom: 1px solid grey;
    border-radius: 0;
    height: calc(0.6em + 0.5rem + 0px) !important;
    color: vt-color(primary);
  }

  .field-edition:focus,
  .field-edition.selected-value {
    border-color: #80bdff !important;
  }

</style>

<style lang="scss" scoped>

   .text-templates-table {
       position: relative;
       overflow: hidden;
       margin: 1rem 0;
       border: 1px solid vt-color(general-border);
   }

  .template-sidepanel {
    width: 100%;
    background: #FFF;
    z-index: 3;
    border-left: 1px solid #dee2e6;
    box-shadow: -5px 0px 6px 0px #9c9c9c;
    height: 100%;
  }

  .template-sidepanel .header {
    border-bottom: 1px solid #dee2e6;
  }

  .template-sidepanel .footer {
    border-top: 1px solid #dee2e6;
    position: absolute;
    bottom: 0;
    width: 100%;
  }

  .text-edition {
    color: #303133;
    line-height: 35px;
    text-align: justify;
    overflow-y: auto;
    height: calc(100% - 90px)
  }

  .panel-visible {
    position: absolute;
    top: 0;
    right: -620px;
    height: 100%;
    width: 610px;
    transition: .3s;
  }

  .active {
    right: 0;
  }

  .templates-modal {
    color: #303133;
  }

  .templates-modal .filters-header input {
    width: 100%;
  }

  .templates-modal .filters-header span {
    font-size: .9rem;
  }

  .templates-modal .table-header {
    background: #f4f4f4;
    border: 1px solid #dee2e6;
    color: rgb(48, 49, 51);
  }

  .templates-modal .table-row {
    border-bottom: 1px solid #dee2e6;
    font-size: .9rem;
    transition: .3s;
    color: rgb(48, 49, 51);
  }

  .templates-modal .table-row:hover {
    background: #f9f9f9;
    cursor: pointer;
  }

  .templates-modal .table-row .table-row-tittle {
    transition: .3s;
  }

  .templates-modal .table-row:hover .table-row-tittle {
    color: vt-color(primary);
  }

  .templates-modal .table-row .table-row-desc {
    text-align: justify;
  }

  .templates-modal .row-active {
    border-left: 3px solid vt-color(primary);
    background: #f9f9f9;
  }

  .templates-modal .row-active .table-row-tittle {
    color: vt-color(primary);
  }

  .templates-modal .table-content {
    height: calc(85vh - 120px);
    min-height: bp(phablet);
    overflow: auto;
    border-left: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
  }

  .templates-modal .btn-filter {
    display: none;
  }

  .templates-modal .filter-input:focus {
    border-color: vt-color(primary);
  }

  .templates-modal .btn-filter {
    position: absolute;
    right: 5px;
    top: 0;
  }

</style>
